<template>
  <ChatModal
    :name="modalName"
    :title="title"
    width="500"
    @before-open="beforeOpen"
  >
    <div slot="default" :class="$style.container">
      <tabs
        :tabs="tabs"
        :currentTab="currentLanguage"
        :class="$style.tabs"
        @onClick="onTabClick"
      />
      <BaseInput
        v-model="currentName"
        :label="$t('blog.name')"
        :invalid="$v.currentName.$error"
        :error="nameError"
        @blur="$v.currentName.$touch"
      />

      <BaseInput
        :label="$t('blog.description')"
        v-model="currentDescription"
        multiline rows="3"
        :helpText="descriptionCounter"
        :invalid="$v.currentDescription.$error"
        :error="descriptionError"
        @blur="$v.currentDescription.$touch"
      />

      <BaseSelect
        v-model="category"
        :label="$t('category')"
        :placeholder="$t('chooseCategory')"
        :options="categories"
        trackBy="id"
        :optionLabel="localeLabel"
        :invalid="$v.categoryId.$error"
        :error="$t('errorEmptyField')"
      >
        <template v-slot:singleLabel="{ option }">
          <Category :category="option" />
        </template>
        <template v-slot:option="{ option }">
          <Category :category="option" />
        </template>
      </BaseSelect>

      <BaseSwitch v-model="isAdult" class="form-container">{{ $t('blog.isAdult') }}</BaseSwitch>
      <BaseSwitch v-model="isPrivate" class="form-container">{{ $t('blog.isPrivate') }}</BaseSwitch>
      <BaseSwitch v-model="isHideOwner" class="form-container">{{ $t('blog.isHideOwner') }}</BaseSwitch>

      <BaseSelect
        v-model="defaultLanguage"
        :label="$t('blog.defaultLanguage')"
        :placeholder="$t('chooseLanguage')"
        :options="locales"
        trackBy="value"
        optionLabel="title"
        :invalid="$v.defaultLanguage.$error"
        :error="$t('errorEmptyField')"
      />

      <div v-if="modalName === 'edit-blog'" :class="$style.bottomBlock">
        <span @click="deleteBlog" :class="$style.deleteBtn">{{ $t('blog.delete') }}</span>
      </div>
    </div>

    <template v-slot:footer>
      <BaseButton oval @click="onSubmit" :loading="loading" :disabled="isButtonDisabled">{{ buttonTxt }}</BaseButton>
    </template>
  </ChatModal>
</template>

<script>
import ChatModal from './ChatModal'
import { mapActions, mapGetters } from 'vuex'
import { required, maxLength } from 'vuelidate/lib/validators'
import Category from '@/components/Category'
import { camelCase, capitalize, snakeCase } from 'lodash'
import { LOCALES } from '@/utils/config'
import Tabs from 'vue-tabs-with-active-line'

const NAME_MAX_LENGTH = 50
const DESCRIPTION_MAX_LENGTH = 500

export default {
  name: 'create-blog-popup',
  components: {
    ChatModal,
    Category,
    Tabs
  },
  data () {
    const blogInfo = {}
    Object.keys(LOCALES).forEach(key => {
      blogInfo[snakeCase(key)] = { name: '', description: '' }
    })
    const currentLanguage = snakeCase(this.$root.$i18n.locale)
    const defaultLanguage = {
      title: capitalize(currentLanguage),
      value: currentLanguage
    }
    return {
      modalName: 'create-blog',
      loading: false,
      isAdult: false,
      isPrivate: true,
      isHideOwner: false,
      category: null,
      title: this.$t('blog.createTitle'),
      buttonTxt: this.$t('buttonCreate'),
      defaultLanguage,
      currentLanguage,
      blogInfo
    }
  },
  computed: {
    user () {
      return this.$store.state.auth.user
    },
    localeLabel () {
      return camelCase(this.$i18n.locale) // zhHans
    },
    ...mapGetters({
      'categories': 'entities/blogCategories/list'
    }),
    categoryId () {
      return this.category ? this.category.id : null
    },
    nameError () {
      if (!this.$v.currentName.maxLength) {
        return `${this.$t('errorMaxLength_begin')} ${NAME_MAX_LENGTH} ${this.$t('errorLength_end')}`
      }
      return this.$t('errorEmptyField')
    },
    descriptionError () {
      if (!this.$v.currentDescription.maxLength) {
        return `${this.$t('errorMaxLength_begin')} ${DESCRIPTION_MAX_LENGTH} ${this.$t('errorLength_end')}`
      }
      return this.$t('errorEmptyField')
    },
    isButtonDisabled () {
      return false
    },
    descriptionCounter () {
      return `${this.currentDescription.length}/${DESCRIPTION_MAX_LENGTH}`
    },
    locales () {
      return Object.keys(LOCALES).map(key => ({
        title: LOCALES[key],
        value: snakeCase(key)
      }))
    },
    tabs () {
      return Object.keys(LOCALES).map(key => ({
        title: capitalize(key),
        value: snakeCase(key)
      }))
    },
    currentName: {
      get () {
        return this.blogInfo[this.currentLanguage].name
      },
      set (value) {
        this.blogInfo[this.currentLanguage].name = value
      }
    },
    currentDescription: {
      get () {
        return this.blogInfo[this.currentLanguage].description
      },
      set (value) {
        this.blogInfo[this.currentLanguage].description = value
      }
    },
    blogInfoList () {
      return Object.keys(this.blogInfo).map(key => {
        const obj = this.blogInfo[key]
        if (!obj.name || !obj.description) return
        return {
          name: obj.name,
          description: obj.description,
          language: key
        }
      }).filter(obj => obj != null)
    }
  },
  watch: {
    currentLanguage () {
      this.$v.currentName.$reset()
      this.$v.currentDescription.$reset()
    }
  },
  validations: {
    currentName: {
      required,
      maxLength: maxLength(NAME_MAX_LENGTH),
      custom () {
        return !!this.blogInfo[this.defaultLanguage.value].name
      }
    },
    currentDescription: {
      required,
      maxLength: maxLength(DESCRIPTION_MAX_LENGTH),
      custom () {
        return !!this.blogInfo[this.defaultLanguage.value].description
      }
    },
    categoryId: { required },
    defaultLanguage: { required }
  },
  methods: {
    ...mapActions({
      getBlogCategories: 'entities/blogCategories/fetch'
    }),
    beforeOpen () {
      this.getBlogCategories()
      this.$v.$reset()
      this.loading = false
      this.category = null
      this.isAdult = false
      this.isPrivate = false
      this.isHideOwner = false
      const defaultLang = snakeCase(this.$root.$i18n.locale)
      this.currentLanguage = defaultLang
      this.defaultLanguage = this.locales.find(obj => obj.value === defaultLang)
      Object.keys(this.blogInfo).forEach(key => {
        this.blogInfo[key].name = ''
        this.blogInfo[key].description = ''
      })
    },
    ...mapActions('entities/blogs', {
      createBlog: 'createBlog'
    }),
    async onSubmit () {
      this.$v.$touch()
      if (this.loading || this.$v.$invalid) return
      this.loading = true
      try {
        const blog = await this.createBlog({
          blog: {
            defaultLanguage: this.defaultLanguage.value,
            isAdult: this.isAdult,
            isPrivate: this.isPrivate,
            categoryId: this.categoryId,
            blogInfo: this.blogInfoList
          },
          blogInfoList: this.blogInfoList
        })
        this.$modal.hide(this.modalName)
        this.$router.push({ name: 'app-blogs', params: { blogId: blog.shortId } })
      } catch (e) {
        const error = e.response.data
        this.$modal.show('error', { error })
      } finally {
        this.loading = false
      }
    },
    onTabClick (newTab) {
      this.currentLanguage = newTab
    }
  }
}
</script>

<style lang="scss" module>
.container {
  padding: 14px 30px 0;
}
.bottomBlock {
  margin: 25px 0;
}
.deleteBtn {
  color: $text-error;
  font-size: 14px;
  cursor: pointer;
}
.tabs:global(.tabs) {
  margin-bottom: 12px;
  :global(.tabs__item) {
    flex-grow: unset;
    flex-basis: auto;
  }
}
</style>
